
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {
	PermissionsGroup,
} from '@/store/models.def';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import { addComma } from '@/util/number';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		TableFilterable,
	},
})
export default class StockAvailabilityTable2 extends FilterWidget {
	@Prop({ default: () => [] })
	public readonly selectedItems!: string[];
	@Prop({ default: '' })
	public readonly selectedGSM!: string;
	@Prop({ default: '' })
	public readonly selectedSize!: string;
	@Prop({ default: '' })
	public readonly selectedBrand!: string;

	public get permissionIds(): PermissionsGroup[] {
		return ['stocks'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public tableItems: Array<{
		item_code: string;
		item_description: string;
		avail_qty: number;
		remark_1: string;
		remark_2: string;
		incoming_Qty: number;
		items: any;
	}> = [];
	public tableFields: any[] = [];
	public detailFields: any[] = [];
	public GSMList: number[] = [];
	public sizeList: string[] = [];
	public brandList: string[] = [];

	@Watch('GSMList', { immediate: true })
	public onGSMListChanged(newVal) {
		this.$emit('returnSelectedGSMList', newVal);
	}

	@Watch('sizeList', { immediate: true })
	public onSizeListChanged(newVal) {
		this.$emit('returnSizeList', newVal);
	}

	@Watch('brandList', { immediate: true })
	public onBrandListChanged(newVal) {
		this.$emit('returnBrandList', newVal);
	}

	public format(
		value: number,
		decimal: number = 2,
		forceDecimal: boolean = true,
	) {
		return addComma(value, decimal, forceDecimal);
	}

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get expensiveHook() {
		const { selectedItems, selectedGSM, selectedSize, selectedBrand } = this;
		return JSON.stringify([
			selectedItems,
			selectedGSM,
			selectedSize,
			selectedBrand,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const queryHead = await this._ensureTablesLoaded(
			['stockUtdBalance', 'stockPurchaseOrder'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.4, 0.6);

		const balTable = getTableName('stockUtdBalance');
		const poTable = getTableName('stockPurchaseOrder');

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const itemFilter = this.selectedItems && this.selectedItems.length > 0
			? ` AND "ITEMCODE" IN (${this.selectedItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		const allRows = await queryHead.rawQuery(`
			SELECT "ITEMCODE" as itemCode, "DESCRIPTION" as descr,
				"GSM" as gsm, "SIZE" as sz,
				TRY_CAST("QTY" AS DOUBLE) as qty,
				TRY_CAST("POQTY" AS DOUBLE) as poQty,
				"BRAND" as brand, "REMARK1" as remark1, "REMARK2" as remark2
			FROM ${balTable}
			WHERE (TRY_CAST("QTY" AS DOUBLE) > 0 OR TRY_CAST("POQTY" AS DOUBLE) > 0)${itemFilter}
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.6, 0.8);

		const initItems = allRows.map((r: any) => ({
			itemCode: String(r.itemCode || ''),
			itemDesc: String(r.descr || ''),
			gsm: r.gsm,
			size: String(r.sz || ''),
			quantity: Number(r.qty) || 0,
			poQuantity: Number(r.poQty) || 0,
			brand: String(r.brand || ''),
			remark1: String(r.remark1 || ''),
			remark2: String(r.remark2 || ''),
		}));

		this.brandList = [...new Set(initItems.map((item) => item.brand))] as string[];

		const brandFiltered = this.selectedBrand
			? initItems.filter((item) => item.brand === this.selectedBrand)
			: initItems;

		this.GSMList = ([...new Set(brandFiltered.map((item) => Number(item.gsm)))] as number[])
			.sort((a, b) => a - b);

		const gsmFiltered = brandFiltered.filter(
			(item) => String(item.gsm) === String(this.selectedGSM),
		);

		this.sizeList = ([...new Set(gsmFiltered.map((item) => item.size))] as string[])
			.sort((a: any, b: any) => a - b);

		let cards: any[];
		if (this.selectedGSM !== null && this.selectedSize !== null) {
			cards = gsmFiltered.filter((item) => item.size === this.selectedSize);
		} else if (
			this.selectedBrand !== null &&
			(this.selectedGSM === null || this.selectedGSM === '')
		) {
			cards = brandFiltered;
		} else if (
			(this.selectedBrand !== null || this.selectedBrand !== '') &&
			this.selectedGSM !== null &&
			this.selectedSize === null
		) {
			cards = gsmFiltered;
		} else {
			cards = initItems;
		}

		const poItemSet: { [k: string]: boolean } = {};
		for (const c of cards) { poItemSet[String(c.itemCode)] = true; }
		const poItemCodes: string[] = Object.keys(poItemSet);
		let poRows: any[] = [];
		if (poItemCodes.length > 0) {
			const poItemFilter = `"ITEMCODE" IN (${poItemCodes.map((s) => `'${escapeStr(s)}'`).join(', ')})`;
			poRows = await queryHead.rawQuery(`
				SELECT "DOCNO" as docNo, "ITEMCODE" as itemCode,
					"DELIVERYDATE" as deliveryDate,
					TRY_CAST("OSQTY" AS DOUBLE) as qty,
					"REMARK1" as remark1
				FROM ${poTable}
				WHERE ${poItemFilter}
			`);
		}
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.8, 0.95);

		const poByItem: { [key: string]: any[] } = {};
		for (const po of poRows) {
			const code = String(po.itemCode || '');
			if (!poByItem[code]) { poByItem[code] = []; }
			poByItem[code].push({
				PO_NO: String(po.docNo || ''),
				quantity: Number(po.qty) || 0,
				ETA: Number(po.deliveryDate) || 0,
				remark_1: String(po.remark1 || ''),
			});
		}

		this.tableItems = cards.map((item) => ({
			item_code: item.itemCode,
			item_description: item.itemDesc,
			avail_qty: item.quantity,
			remark_1: item.remark1,
			remark_2: item.remark2,
			incoming_Qty: item.poQuantity,
			items: poByItem[item.itemCode] || [],
		}));

		this.tableFields = [
			{ key: 'item_description', sortable: true, stickyColumn: true },
			{
				key: 'avail_qty',
				label: 'Avail.Qty',
				sortable: true,
				formatter: TableItemFormatter.basicInteger,
			},
			{
				key: 'remark_1',
				sortable: true,
			},
			{
				key: 'incoming_Qty',
				sortable: true,
			},
			{
				key: 'remark_2',
				sortable: true,
			},
		];

		this.detailFields = [
			{ key: 'PO_NO', label: 'PO No.', sortable: true },
			{
				key: 'quantity',
				sortable: true,
				formatter: TableItemFormatter.basicInteger,
			},
			{
				key: 'ETA',
				sortable: true,
				formatter: TableItemFormatter.date3,
			},
			{
				key: 'remark_1',
				sortable: true,
			},
		];

		this.saveHistory(
			'tableItems',
			'tableFields',
			'detailFields',
			'GSMList',
			'sizeList',
			'brandList',
		);
		this.generateLoadingText(1);
	}
}
